/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import styled from '@emotion/styled';
import { Card } from '@material-ui/core';

import { ICustomTheme } from 'interfaces';
import { WidgetsType } from './types';
import { WIDGETS_TYPE } from './constants';
import { TCurrentGlobalViewWidgetSet } from 'interfaces/widgets.types';
import WidgetContent from './WidgetContent';

const StyledCard = styled(Card)<{ theme: ICustomTheme }>(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 3,
  boxShadow: theme.boxShadow,
}));

const StyledContainer = styled.div<{ theme: ICustomTheme }>(({ theme }) => ({
  padding: theme.spacing(0, 8),
  [theme.breakpoints.down('sm')]: {
    height: 'auto',
    padding: theme.spacing(0, 3.5),
  },
}));

const StyledContent = styled((props) => (
  <StyledCard elevation={3} {...props} />
))();

const cssAlignInitial = css({
  textAlign: 'initial',
});
const cssCard = css({
  height: '100%',
  width: '100%',
});
const cssContainer = (theme: ICustomTheme) => [
  cssAlignInitial,
  {
    alignItems: 'center',
    display: 'flex',
    height: theme.layout.widgets.widgetHeight,
  },
];
const cssContainerFullWidth = (theme: ICustomTheme) => [
  cssAlignInitial,
  { margin: theme.spacing(-1, -1.5) },
];
const cssContainerThird = (theme: ICustomTheme) => [
  cssContainer(theme),
  { margin: theme.spacing(0, 1.5) },
];

interface TProps {
  currentGlobalViewWidgetSet?: TCurrentGlobalViewWidgetSet;
  widget: WidgetsType;
  half: boolean;
  third: boolean;
  isFullWidth: boolean;
  noMarginAdjust: boolean;
  isDisplayOnWidgetDouble?: boolean;
}

const Widget = ({
  currentGlobalViewWidgetSet,
  widget,
  half,
  isDisplayOnWidgetDouble = false,
  third,
  isFullWidth,
  noMarginAdjust,
}: TProps): JSX.Element => {
  if (isDisplayOnWidgetDouble) {
    return (
      <WidgetContent
        widget={widget}
        isDisplayOnWidgetDouble
        currentGlobalViewWidgetSet={currentGlobalViewWidgetSet}
      />
    );
  }

  if (half) {
    return (
      <StyledContent css={cssContainer}>
        <WidgetContent
          widget={widget}
          half
          currentGlobalViewWidgetSet={currentGlobalViewWidgetSet}
        />
      </StyledContent>
    );
  }

  if (third) {
    return (
      <StyledContent css={cssContainerThird}>
        <WidgetContent
          widget={widget}
          half
          currentGlobalViewWidgetSet={currentGlobalViewWidgetSet}
        />
      </StyledContent>
    );
  }

  if (
    isFullWidth ||
    widget['@type'] === WIDGETS_TYPE.EDITO ||
    widget['@type'] === WIDGETS_TYPE.ACTION_GLOBAL_VIEW ||
    widget['@type'] === WIDGETS_TYPE.GAME
  ) {
    return (
      <div css={noMarginAdjust ? cssAlignInitial : cssContainerFullWidth}>
        <WidgetContent
          widget={widget}
          currentGlobalViewWidgetSet={currentGlobalViewWidgetSet}
        />
      </div>
    );
  }

  return (
    <StyledContainer css={cssContainer}>
      <StyledContent css={cssCard}>
        <WidgetContent
          widget={widget}
          currentGlobalViewWidgetSet={currentGlobalViewWidgetSet}
        />
      </StyledContent>
    </StyledContainer>
  );
};

Widget.defaultProps = {
  half: false,
  third: false,
  isFullWidth: false,
  noMarginAdjust: false,
};

export default Widget;
